import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getExam } from 'FRONTEND/components/Surveillance/utils/getExam';
import { normalizeExamName } from 'FRONTEND/components/Surveillance/utils/normalizeExamName';
import { useGetMedicineNamesByIdsQuery, useListDrugReferencesQuery } from 'FRONTEND/utils/backendApi/drugReferencesApi';
import { SURVEILLANCE_EXAM_NAME } from 'FRONTEND/utils/constants';
import { useCurrentWorkflow } from 'FRONTEND/utils/nonSerializableStorage/useCurrentWorkflow';

const getMedicineChoiceIds = medicines => medicines.flatMap(
  medicine => typeof medicine === 'string' ? [medicine] : [medicine?.id, medicine?.value],
).filter(Boolean);

export const getReferencedMedicineIds = ({ surveillanceEvents, medicineExamName, medicineFieldId }) => {
  const referencedIds = new Set();
  for (const event of surveillanceEvents) {
    const examName = event.examName ?? event.type;
    if (normalizeExamName(examName).toLowerCase() !== normalizeExamName(medicineExamName).toLowerCase()) {
      continue;
    }
    // History entries can refer to a different drug. Never send the free-text "other" name.
    for (const entry of [event, ...(event.modificationHistory ?? [])]) {
      const entryValues = entry.values?.[examName] ?? Object.entries(entry.values ?? {}).find(
        ([entryExamName]) => normalizeExamName(entryExamName).toLowerCase() === normalizeExamName(medicineExamName).toLowerCase(),
      )?.[1];
      const storedMedicine = entryValues?.[medicineFieldId];
      const medicineId = typeof storedMedicine === 'object' ? storedMedicine?.value : storedMedicine;
      if (typeof medicineId === 'string' && medicineId) {
        referencedIds.add(medicineId);
      }
    }
  }
  // The production TypeScript pass downlevels iterable spread with an array-only helper, which turns
  // `[...referencedIds]` into an empty array because Set has no `length`. Array.from works in both the
  // Jest/Babel and production webpack compilation paths.
  return Array.from(referencedIds).sort();
};

export const getMissingMedicineIds = ({ surveillanceEvents, medicines, medicineExamName, medicineFieldId }) => {
  const knownIds = new Set(getMedicineChoiceIds(medicines));
  return getReferencedMedicineIds({ surveillanceEvents, medicineExamName, medicineFieldId })
    .filter(medicineId => !knownIds.has(medicineId));
};

// Use same function as MC
// TO DO > homogenize surveillanceEventType (in MC, we pass object as paramter and surveillanceType can be surveillance.type or surveillance.examName)
export const useMedicineList = (form, surveillanceEventType, tenantId) => {
  const currentWorkflow = useCurrentWorkflow();
  const surveillanceEvents = useSelector(state => state.consultation.currentConsultation.surveillanceList);

  // We include deleted here so we can still display medicines that have been deleted for a given consultation.
  const { currentData: drugReferences, isFetching, isUninitialized } = useListDrugReferencesQuery({ tenantId, includeDeleted: true }, {
    skip: !tenantId,
  });

  const medicineCloudAPIFieldPath = currentWorkflow?.genericFieldPaths?.medicine_choices?.surveillance_medicine;
  const medicineJSONListFieldPath = currentWorkflow?.genericFieldPaths?.medicine_choices?.surveillance_medicine_json_list;
  const fieldArray = useMemo(() => {
    const fieldArray = [];
    for (const fieldpath of [medicineCloudAPIFieldPath, medicineJSONListFieldPath].filter(Boolean)) {
      const formToConsider = getExam({
        workflow: currentWorkflow,
        examId: fieldpath.parentExam,
        // Resolve fresh from the current workflow. getExam's module-level cache is
        // first-write-wins and never invalidated, so a shared entry can silently return
        // the wrong exam here depending on what rendered first.
        skipCache: true,
      });

      if (!formToConsider || !formToConsider.pages) {
        continue;
      }
      for (const page of formToConsider.pages) {
        const field = findDeeplyNestedField(fieldpath.fieldId, page.fields);
        if (field) {
          fieldArray.push(field);
          break;
        }
      }
    }
    // loop through the pages until we find the correct field

    return fieldArray;
    // `form` is intentionally not a dependency: the fields are resolved from
    // `currentWorkflow` via `getExam`, not from the passed-in `form`.
    // The field paths are included so the resolve re-runs when the workflow hydrates/reloads.
  }, [medicineCloudAPIFieldPath, medicineJSONListFieldPath, currentWorkflow]);

  // TO DO > get ride of the hardcoded id. Same on station side
  const medicines = useMemo(() => {
    if (!currentWorkflow?.genericFieldPaths) {
      return normalizeExamName(surveillanceEventType).toLowerCase() === SURVEILLANCE_EXAM_NAME.MEDICINE ? form?.pages?.[0]?.fields?.find(
        (field) => ['surveillance_medicine', 'liste_medicine_intervention_reasons'].includes(field.id),
      )?.choices ?? [] : [];
    }
    if (fieldArray?.length === 0) {
      // we try a backward compatible route to the choices in question
      const medicineExamChoices = currentWorkflow?.transitions?.create.exams_full.find(
        (exam) => exam.id === medicineCloudAPIFieldPath?.parentExam)
        ?.pages?.[0].fields?.find(
          (field) => field.id === medicineCloudAPIFieldPath?.fieldId,
        )?.choices || [];
      const interventionReasonsExamChoices = currentWorkflow?.transitions?.create.exams_full.find(
        (exam) => exam.id === medicineJSONListFieldPath?.parentExam)
        ?.pages?.[0].fields?.find(
          (field) => field.id === medicineJSONListFieldPath?.fieldId,
        )?.choices || [];
      return [...medicineExamChoices, ...interventionReasonsExamChoices];
    }


    const result = [];
    for (const field of fieldArray ?? []) {
      if (field.choicesFromApi) {
        result.push(...(drugReferences ?? []));
      } else {
        result.push(...(field.choices ?? []));
      }
    }
    return result;
  }, [fieldArray, drugReferences, currentWorkflow, medicineCloudAPIFieldPath, medicineJSONListFieldPath, form, surveillanceEventType]);

  const medicineExamName = currentWorkflow?.medicineExam ?? medicineCloudAPIFieldPath?.parentExam ?? 'smur_idf/medicine';
  const medicineFieldId = medicineCloudAPIFieldPath?.fieldId ?? 'surveillance_medicine';
  const waitingForTenantMedicines = !drugReferences && (isFetching || isUninitialized);


  // Deliberately not memoized: Redux DevTools injection and consultation hydration can preserve the
  // outer array/object references while changing their contents. The scan is small, and recalculating
  // prevents a stale empty result from permanently keeping the RTK Query hook in its skipped state.
  let missingMedicineIds = [];
  if (!waitingForTenantMedicines) {
    missingMedicineIds = getMissingMedicineIds({
      surveillanceEvents,
      medicines: [...medicines, ...(drugReferences ?? [])],
      medicineExamName,
      medicineFieldId,
    });
  }

  const {
    currentData: medicineNames,
  } = useGetMedicineNamesByIdsQuery(missingMedicineIds, {
    skip: missingMedicineIds.length === 0,
  });

  // Display only: foreign drugs must not become choices for prescribing on this tenant.
  return useMemo(() => [
    ...medicines,
    ...Object.entries(medicineNames ?? {}).map(([id, name]) => ({
      id,
      value: id,
      label: name,
      commercial_name: name,
      dci: '',
      mode_administration: '',
      sub_category: '',
    })),
  ], [medicines, medicineNames]);
};

const findDeeplyNestedField = (fieldIdToFind, fieldArray) => {
  if (!fieldArray) {
    return null;
  }
  for (const field of fieldArray) {
    if (field.id === fieldIdToFind) {
      return field;
    }
    const nested = findDeeplyNestedField(fieldIdToFind, field.fields);
    if (nested) {
      return nested;
    }
  }
  return null;
};
